import axios from 'axios';
import Card from 'components/Card/Card';
import React, { useEffect, useState, useMemo } from 'react';
import propTypes, { object } from 'prop-types';
import {
  ArrowLeft,
  ChevronRight,
  Settings,
  Download,
  Info
} from 'lucide-react';
import style from './AgencyDashboard.module.scss';
import Button from 'tailwind-components/Button/Button';
import AgentsTable from './AgentsTable/AgentsTable';
import SunshineZohoFormModal from 'components/SunshineZohoFormModal/SunshineZohoFormModal';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CircularProgress,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip
} from '@mui/material';
import AutocompleteInput from 'components/Autocomplete/AutocompleteInput';
import AgentsFiltersToolbar from 'tailwind-components/AgentsFiltersToolbar/AgentsFiltersToolbar';
import {
  AGENT_FILTER_ALL,
  DEFAULT_SORT_OPTION,
  filterAndSortAgents,
  getAgentPositionOptions
} from 'helpers/agencyManagement/agent-filters';
import { getCompany } from 'helpers/getCompanyVariables';
import { isOwnAgency } from 'helpers/agencyManagement/agency-id';
import usePendingAppointments from '../AgencyAppointments/usePendingAppointments';
import { useSelector } from 'react-redux';
import { areOnboardingInvitesDisabled } from 'helpers/viewing-rules';

const company = getCompany();
const SUNSHINE_DOWNLINE_UPDATE_FORM_BASE =
  'https://forms.zohopublic.com/sunshinelifehealthadvisors/form/AgencyDownlineUpdateIframe/formperma/J1X7sL014AnLiRFvb4vYtG7k96EJBcPyywZfk-654I8';

const INITIAL_SUNSHINE_MODAL_DATA = {
  title: '',
  iframeTitle: '',
  src: ''
};

const AgencyDashboard = ({
  agencyData,
  agencyDashboardAgents,
  Confirmed_Appointments_Count,
  setLocalLoader,
  localLoader,
  getSpecificAgencyData,
  downloadAppointmentsReport,
  getSelectedAgenciesAgentsAndAppointmentCount,
  refreshUser
}) => {
  // Axios
  const cancelTokenSource = axios.CancelToken.source();

  //Hooks
  const { id } = useParams();
  const navigate = useNavigate();

  //States
  const [multiselectSelectedOptions, setmultiselectSelectedOptions] = useState(
    []
  );
  const [showOnlyActiveAgents, setShowOnlyActiveAgents] = useState(true);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState(AGENT_FILTER_ALL);
  const [sortOption, setSortOption] = useState(DEFAULT_SORT_OPTION);
  const [includeInactiveAgents, setIncludeInactiveAgents] = useState(false);
  const [sunshineModalData, setSunshineModalData] = useState(
    INITIAL_SUNSHINE_MODAL_DATA
  );

  const zohoData = useSelector(state => state.user.zohoData);
  const userAgencyData = zohoData?.agencyData;
  const currentAgency = agencyData?.agencyInformation;

  // Agency_Downline and Approve_Downline only live in zoho, and the portal only holds
  // the zoho record of the logged agent's own agency — aws' /agencies/selected doesn't
  // return them. so a parent agency opening a downline agency can't be gated correctly
  // and gets neither the button nor the pending note
  const viewingOwnAgency = isOwnAgency(currentAgency, zohoData);
  const existingDownline = userAgencyData?.Agency_Downline?.length > 0;
  const approvedDownline = userAgencyData?.Approve_Downline;
  const downlinePendingApproval = existingDownline && !approvedDownline;
  const canAddDownline = viewingOwnAgency && !downlinePendingApproval;
  const showDownlinePendingNote = viewingOwnAgency && downlinePendingApproval;

  function buildSunshineDownlineUpdateFormUrl(agency) {
    const params = new URLSearchParams();
    params.set('FEIN', agency?.FEIN || '');
    params.set('Agency_ID', agency?.id?._value || '');
    params.set('Agency_Name', agency?.name?._value || '');
    return `${SUNSHINE_DOWNLINE_UPDATE_FORM_BASE}?${params.toString()}`;
  }

  const invitesDisabled = areOnboardingInvitesDisabled(userAgencyData);

  const multiselectAvailableOptions = useMemo(() => {
    if (agencyData?.agencyInformation.Downline_Agencies_Three) {
      // Sets main agency as default
      setmultiselectSelectedOptions([
        {
          name: agencyData.agencyInformation.name._value,
          value: agencyData.agencyInformation.id._value
        }
      ]);
      // Gets the agents of the default agency into the table
      getSelectedAgenciesAgentsAndAppointmentCount(
        setLocalLoader,
        [agencyData.agencyInformation.id._value],
        cancelTokenSource
      );
      // Returns member agencies as options
      return [
        agencyData.agencyInformation,
        ...agencyData.agencyInformation.Downline_Agencies_Three
      ].map(a => ({
        name: a?.name?._value,
        value: a?.id?._value
      }));
    }
    return [];
  }, [agencyData]);

  // the agencies the pending count is currently loaded for. the route agency until
  // the user updates the table with a different selection. null means the route
  // agency has not been resolved yet, which reads as loading rather than as zero.
  const [pendingAgencies, setPendingAgencies] = useState(null);

  const {
    pendingAppointments,
    loadingPendingAppointments,
    refetchPendingAppointments,
    exportPendingAppointments
  } = usePendingAppointments(pendingAgencies);

  const pendingLoading = loadingPendingAppointments || pendingAgencies === null;

  useEffect(() => {
    (async () => {
      if (id) {
        refreshUser(() => {}, cancelTokenSource);
        await getSpecificAgencyData(setLocalLoader, id, cancelTokenSource);
        setPendingAgencies([{ agencyId: String(id), agencyName: '' }]);
      } else navigate('/agencies');
    })();

    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  const [timesInputClosed, setTimesInputClosed] = useState(0);

  //Action handlers
  const handleChangeSelectValue = selectedOptions => {
    setmultiselectSelectedOptions(selectedOptions);
  };

  const handleUpdateTable = async () => {
    const selected_agencies_ids = multiselectSelectedOptions.map(
      option => option.value
    );
    setPendingAgencies(
      multiselectSelectedOptions.map(o => ({
        agencyId: String(o.value),
        agencyName: o.name
      }))
    );
    await getSelectedAgenciesAgentsAndAppointmentCount(
      setLocalLoader,
      selected_agencies_ids,
      cancelTokenSource
    );
    setTimesInputClosed(timesInputClosed + 1);
  };

  const exportConfirmedAppointmentsHandler = () => {
    const selected_ids = multiselectSelectedOptions.map(option => option.value);
    // If includeInactiveAgents is false, only export active agents (onlyActive = true)
    const onlyActive = !includeInactiveAgents;
    downloadAppointmentsReport(setLocalLoader, selected_ids, onlyActive);
  };

  const closeSunshineDownlineModalHandler = async () => {
    setSunshineModalData(INITIAL_SUNSHINE_MODAL_DATA);
    if (!id) return;
    refreshUser(() => {}, cancelTokenSource);
    await getSpecificAgencyData(setLocalLoader, id, cancelTokenSource);
    // the downline may have changed, so re-read rather than trusting the cache
    setPendingAgencies([{ agencyId: String(id), agencyName: '' }]);
    refetchPendingAppointments();
  };

  const filteredAgents = useMemo(() => {
    if (showOnlyActiveAgents) {
      return agencyDashboardAgents.filter(agent => agent.rank !== 'Inactive');
    }
    return agencyDashboardAgents;
  }, [agencyDashboardAgents, showOnlyActiveAgents]);

  // Update Table swaps the agent set without a route change, so a position that
  // no longer exists in the new selection would silently empty the table
  useEffect(() => {
    if (positionFilter === AGENT_FILTER_ALL) return;
    if (filteredAgents.length === 0) return;
    if (!getAgentPositionOptions(filteredAgents).includes(positionFilter)) {
      setPositionFilter(AGENT_FILTER_ALL);
    }
  }, [filteredAgents, positionFilter]);

  const displayAgents = useMemo(
    () =>
      filterAndSortAgents({
        agents: filteredAgents,
        searchQuery: agentSearchQuery,
        positionFilter,
        sortOption
      }),
    [filteredAgents, agentSearchQuery, positionFilter, sortOption]
  );

  const agentCounts = useMemo(() => {
    const activeCount = agencyDashboardAgents.filter(
      agent => agent.rank !== 'Inactive'
    ).length;
    const inactiveCount = agencyDashboardAgents.filter(
      agent => agent.rank === 'Inactive'
    ).length;
    return { activeCount, inactiveCount };
  }, [agencyDashboardAgents]);

  return (
    <div className={style.dashboard}>
      <div className={style.agenciesItem}>
        <div className={style.memberAgencies}>
          <Link to='/agencies'>
            <Button
              text='Agencies'
              variant='secondary'
              outlined
              size='small'
              rounded='large'
              icon={<ArrowLeft size={24} />}
            />
          </Link>
          <div className={style.separator}>
            <ChevronRight />
          </div>
          {localLoader ? (
            <CircularProgress size={20} />
          ) : (
            <Button
              text={agencyData?.agencyInformation?.name?._value || ''}
              variant='default'
              outlined
              size='small'
              rounded='large'
            />
          )}
        </div>
        <Stack mb={0} marginTop={4}>
          <AutocompleteInput
            insideButton={
              <Button
                text='Update Table'
                variant='primaryLight'
                onClick={() => handleUpdateTable()}
              />
            }
            multiple
            options={multiselectAvailableOptions}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            id='agencies'
            value={multiselectSelectedOptions}
            getOptionLabel={option => option.name}
            onValueChanges={e => handleChangeSelectValue(e)}
            disabled={false}
            selectAll
            renderInput={params => (
              <TextField
                {...params}
                label={
                  multiselectSelectedOptions?.length ===
                  agencyData?.agencyInformation.Member_Agencies?.length
                    ? 'All Agencies(s)'
                    : 'Agencies(s)'
                }
                placeholder='Select agencies'
                error={false}
                helperText={
                  multiselectSelectedOptions.length > 0 ? (
                    ''
                  ) : (
                    <span style={{ color: 'red' }}>
                      You need to select at least 1 agency
                    </span>
                  )
                }
                InputLabelProps={{
                  shrink: true
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {localLoader ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            timesInputClosed={timesInputClosed}
          />
        </Stack>
      </div>
      <div className={style.infoItem}>
        <Card
          fill
          cardValues
          title={agencyData?.agencyInformation?.name?._value || ''}
          variant='primaryDarkened'
        >
          <div className={style.cardContent}>
            <span>
              Email:{' '}
              {agencyData.agencyInformation.Agency_Email
                ? agencyData.agencyInformation.Agency_Email
                : '---'}
            </span>
            <Link to='settings'>
              <Button
                text='More info'
                variant='default'
                outlined
                size='small'
                rounded='large'
                icon={<Info size={20} />}
              />
            </Link>
          </div>
        </Card>
      </div>
      <div className={style.generalItem}>
        <Card
          fill
          variant='primaryLight'
          cardValues
          title='Selected Agents & Agencies'
        >
          <div className={style.cardContent}>
            <span>Active Agents: {agentCounts.activeCount}</span>
            <span>Inactive Agents: {agentCounts.inactiveCount}</span>
            <span>Agencies: {multiselectSelectedOptions.length || 0}</span>
            {company === 'sunshine' && canAddDownline && (
              <Button
                text='Add Downline Agents'
                variant='default'
                outlined
                size='small'
                rounded='large'
                style={{ marginTop: '20px', width: '50%', textAlign: 'left' }}
                onClick={() =>
                  setSunshineModalData({
                    title: 'Add Downline Agents',
                    iframeTitle: 'Agency Downline Update',
                    src: buildSunshineDownlineUpdateFormUrl(currentAgency)
                  })
                }
              />
            )}
            {company === 'sunshine' && showDownlinePendingNote && (
              <p className={style.downlinePendingNote}>
                If the <b>Add Downline Agents</b> button is not showing, it is
                because your downline agents request is currently being
                approved.
              </p>
            )}
          </div>
        </Card>
      </div>
      <div className={style.appointmentsItem}>
        <Card fill cardValues title='Appointments' type='primaryNavy'>
          <div className={style.appointmentsCard}>
            <div className={style.cardContent}>
              {pendingLoading ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <CircularProgress style={{ color: '#38aaff' }} size={25} />
                  <span
                    style={{
                      fontSize: 'large',
                      marginTop: '5px'
                    }}
                  >
                    Loading pending appointments...
                  </span>
                </div>
              ) : (
                <>
                  <span>Pending: {pendingAppointments.length}</span>
                  <Button
                    text='Export Pending Appointments'
                    variant='default'
                    isDisabled={pendingAppointments.length === 0}
                    outlined
                    size='small'
                    rounded='large'
                    onClick={() =>
                      exportPendingAppointments([
                        'Ticket_ID',
                        'Agent_Name',
                        'NPN',
                        'Carrier_Name',
                        'Appointment_Type',
                        'States'
                      ])
                    }
                    icon={<Download size={20} />}
                  />
                </>
              )}
              <span style={{ marginTop: '10px' }}>
                Confirmed: {Confirmed_Appointments_Count}
              </span>
              <div style={{ marginTop: '10px' }}>
                <Button
                  text='Export Confirmed Appointments'
                  variant='default'
                  outlined
                  size='small'
                  rounded='large'
                  isDisabled={multiselectSelectedOptions.length === 0}
                  onClick={exportConfirmedAppointmentsHandler}
                  icon={<Download size={20} />}
                />
                <FormGroup
                  style={{ marginTop: '2px', display: 'inline-block' }}
                >
                  <Tooltip
                    title='This option only applies to Confirmed Appointments export'
                    placement='bottom'
                  >
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includeInactiveAgents}
                            onChange={e =>
                              setIncludeInactiveAgents(e.target.checked)
                            }
                            size='small'
                            sx={{
                              color: '#ffffff',
                              '&.Mui-checked': {
                                color: '#ffffff'
                              }
                            }}
                          />
                        }
                        label={
                          <span
                            style={{ fontSize: '0.85rem', color: '#ffffff' }}
                          >
                            Include Inactive agents in report
                          </span>
                        }
                        sx={{
                          marginLeft: 0,
                          '& .MuiFormControlLabel-label': {
                            color: '#ffffff'
                          }
                        }}
                      />
                    </span>
                  </Tooltip>
                </FormGroup>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <div className={style.agentsItem}>
        <Card
          fill
          title={`Agents List (${displayAgents.length})`}
          middleSpace={false}
          titleOptionsSide={
            <Stack direction='row' spacing={2} alignItems='center'>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyActiveAgents}
                      onChange={e => setShowOnlyActiveAgents(e.target.checked)}
                      size='small'
                    />
                  }
                  label={
                    <span style={{ fontSize: '0.85rem' }}>
                      Show Only Active Agents
                    </span>
                  }
                  sx={{
                    marginLeft: 0
                  }}
                />
              </FormGroup>
              <span style={{ fontSize: '0.75rem' }}>
                * Updates made every hour
              </span>
            </Stack>
          }
          options={
            <Link to='settings'>
              <Button
                size='medium'
                rounded='large'
                variant='secondary'
                text={invitesDisabled ? 'Settings' : 'Settings/Invites'}
                icon={<Settings size={20} />}
              />
            </Link>
          }
          loading={localLoader}
        >
          <div className='w-full min-w-0 pb-3'>
            <AgentsFiltersToolbar
              agents={filteredAgents}
              searchQuery={agentSearchQuery}
              onSearchChange={setAgentSearchQuery}
              positionFilter={positionFilter}
              onPositionFilterChange={setPositionFilter}
              sortOption={sortOption}
              onSortChange={setSortOption}
            />
          </div>
          <div
            className={style.tableContainer}
            id='agencyDashboardAgentsTableContainer'
          >
            <AgentsTable
              agents={displayAgents}
              onOffboardingSuccess={handleUpdateTable}
            />
          </div>
        </Card>
      </div>
      {!!sunshineModalData.src && (
        <SunshineZohoFormModal
          data={sunshineModalData}
          onClose={closeSunshineDownlineModalHandler}
        />
      )}
    </div>
  );
};

AgencyDashboard.propTypes = {
  agencyData: propTypes.object,
  agencyDashboardAgents: propTypes.arrayOf(object),
  Confirmed_Appointments_Count: propTypes.number,
  refreshUser: propTypes.func,
  setLocalLoader: propTypes.func,
  localLoader: propTypes.bool,
  getSpecificAgencyData: propTypes.func,
  downloadAppointmentsReport: propTypes.func,
  getSelectedAgenciesAgentsAndAppointmentCount: propTypes.func,
  getAgenciesTree: propTypes.func
};

export default AgencyDashboard;
